/* GiftMira — announcement bar behaviour (pairs with sections/gm-announcement-bar.liquid).
   Morph-safe custom element. Re-checks every schedule in the browser, because Shopify can serve a
   cached page after a message's end time. Countdowns only count to a fixed end time. */
(() => {
  if (customElements.get('gm-announcement-bar')) return;
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => matchMedia('(max-width: 749px)').matches;
  const pad = (n) => String(n).padStart(2, '0');

  // Seconds until the next cutoff in a given IANA time zone, skipping weekends and holidays.
  function secondsToCutoff(tz, hour, minute, weekdaysOnly, holidays) {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short', hour12: false });
    const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
    const nowSec = (+p.hour % 24) * 3600 + +p.minute * 60 + +p.second;
    const cutSec = hour * 3600 + minute * 60;
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    let dow = days.indexOf(p.weekday);
    let date = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
    const closed = (d, w) => (weekdaysOnly && (w === 0 || w === 6)) || holidays.includes(d.toISOString().slice(0, 10));
    let add = 0;
    if (closed(date, dow) || nowSec >= cutSec) {
      for (let i = 0; i < 14; i++) {
        add++; dow = (dow + 1) % 7; date = new Date(date.getTime() + 86400000);
        if (!closed(date, dow)) break;
      }
    }
    return add * 86400 + cutSec - nowSec;
  }

  function clockParts(secs, showDays) {
    secs = Math.max(0, Math.floor(secs));
    const d = Math.floor(secs / 86400), h = Math.floor((secs % 86400) / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    const out = [];
    if (d > 0 && showDays) out.push([d, 'd']);
    out.push([showDays || d === 0 ? h : h + d * 24, 'h'], [m, 'm']);
    if (d === 0) out.push([s, 's']);
    return out;
  }
  const clockLabel = (parts) => parts.map(([v, u]) => v + ' ' + ({ d: 'day', h: 'hour', m: 'minute', s: 'second' })[u] + (v === 1 ? '' : 's')).join(' ');

  class GmAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.gab__track');
      if (!this.track) return;
      this.mode = this.dataset.mode;
      this.seconds = Math.max(3, +this.dataset.seconds || 5) * 1000;
      this.duration = +this.dataset.duration || 600;
      this.baseTheme = this.dataset.baseTheme;
      this.index = 0; this.userPaused = false; this.hover = false; this.focus = false; this.visible = true;

      this.querySelectorAll('.gab__code').forEach((btn) => btn.addEventListener('click', () => this.copy(btn)));
      this.querySelector('.gab__close')?.addEventListener('click', () => this.dismiss());
      this.querySelector('.gab__prev')?.addEventListener('click', () => { this.hold(); this.go(this.index - 1); });
      this.querySelector('.gab__next')?.addEventListener('click', () => { this.hold(); this.go(this.index + 1); });
      this.pauseBtn = this.querySelector('.gab__pause');
      this.pauseBtn?.addEventListener('click', () => this.setPaused(!this.userPaused));
      if (this.dataset.hoverPause === 'true') {
        this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hover = true; this.refresh(); } });
        this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hover = false; this.refresh(); } });
      }
      this.addEventListener('focusin', () => { this.focus = true; this.refresh(); });
      this.addEventListener('focusout', () => { setTimeout(() => { this.focus = this.contains(document.activeElement); this.refresh(); }, 0); });
      this.onVis = () => this.refresh();
      document.addEventListener('visibilitychange', this.onVis);
      this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.refresh(); });
      this.io.observe(this);
      this.mq = matchMedia('(max-width: 749px)');
      this.onMq = () => this.rebuild();
      this.mq.addEventListener?.('change', this.onMq);

      this.rebuild();
      this.tickTimer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.tickTimer); clearInterval(this.rotTimer); clearTimeout(this.holdT);
      document.removeEventListener('visibilitychange', this.onVis);
      this.io?.disconnect(); this.mq?.removeEventListener?.('change', this.onMq);
    }

    // Which slides may show right now (schedule + device + cutoff window).
    eligible(slide) {
      const now = Date.now() / 1000;
      const start = +slide.dataset.start || 0, end = +slide.dataset.end || 0;
      if (start && now < start) return false;
      if (end && now >= end && !(slide.dataset.type === 'countdown' && slide.dataset.ended)) return false;
      const dev = slide.dataset.device;
      if (dev === 'mobile' && !isMobile()) return false;
      if (dev === 'desktop' && isMobile()) return false;
      if (slide.dataset.type === 'cutoff') {
        const secs = this.cutoffSecs(slide);
        if (secs > (+slide.dataset.maxHours || 24) * 3600) return false;
      }
      return true;
    }

    cutoffSecs(slide) {
      const hol = (slide.dataset.holidays || '').split(',').map((s) => s.trim()).filter(Boolean);
      return secondsToCutoff(slide.dataset.tz || 'America/Los_Angeles', +slide.dataset.hour || 0, +slide.dataset.minute || 0, slide.dataset.weekdays !== 'false', hol);
    }

    rebuild() {
      const all = [...this.track.querySelectorAll(':scope > .gab__slide')];
      if (this.mode === 'marquee') {
        this.track.querySelectorAll('.gab__slide[data-clone]').forEach((c) => c.remove());
        const live = all.filter((s) => !s.dataset.clone);
        live.forEach((s) => { s.hidden = !this.eligible(s); s.setAttribute('aria-hidden', 'false'); });
        this.slides = live.filter((s) => !s.hidden);
        if (!this.slides.length) return this.empty();
        // Duplicate the set so the ticker loops seamlessly.
        this.slides.forEach((s) => { const c = s.cloneNode(true); c.dataset.clone = '1'; c.setAttribute('aria-hidden', 'true'); c.querySelectorAll('a,button').forEach((x) => x.setAttribute('tabindex', '-1')); this.track.appendChild(c); });
        this.tick();
        // Measure directly (no rAF: hidden tabs and some phones throttle animation frames).
        const w = this.track.scrollWidth / 2;
        this.style.setProperty('--gab-marq', Math.max(8, w / (+this.dataset.speed || 50)) + 's');
        this.applyTheme(this.slides[0]);
        return;
      }
      this.slides = all.filter((s) => this.eligible(s));
      all.forEach((s) => { if (!this.slides.includes(s)) { s.classList.remove('is-on', 'is-out'); s.setAttribute('aria-hidden', 'true'); s.hidden = true; } else s.hidden = false; });
      if (!this.slides.length) return this.empty();
      if (this.index >= this.slides.length) this.index = 0;
      const current = this.slides.find((s) => s.classList.contains('is-on'));
      this.index = current ? this.slides.indexOf(current) : 0;
      this.slides.forEach((s, i) => { s.classList.toggle('is-on', i === this.index); s.setAttribute('aria-hidden', String(i !== this.index)); });
      this.applyTheme(this.slides[this.index]);
      const many = this.slides.length > 1 && this.mode === 'rotate';
      this.querySelectorAll('.gab__prev,.gab__next,.gab__pause').forEach((b) => (b.hidden = !many));
      this.tick();
      this.refresh();
    }

    empty() { this.hidden = true; clearInterval(this.rotTimer); this.rotTimer = null; }

    applyTheme(slide) {
      const t = slide?.dataset.theme;
      if (t && t !== 'inherit') this.dataset.activeTheme = t; else delete this.dataset.activeTheme;
      if (t === 'brand') { this.classList.remove('gab--t-' + this.baseTheme); } else if (this.baseTheme) this.classList.add('gab--t-' + this.baseTheme);
    }

    running() { return !this.userPaused && !this.hover && !this.focus && !this.held && this.visible && !document.hidden && !reduce(); }

    refresh() {
      if (this.mode === 'marquee') { this.classList.toggle('is-paused', !(!this.userPaused && !this.hover && !this.focus && !document.hidden)); return; }
      const go = this.mode === 'rotate' && this.slides?.length > 1 && this.running();
      if (go && !this.rotTimer) this.rotTimer = setInterval(() => this.go(this.index + 1), this.seconds);
      if (!go) { clearInterval(this.rotTimer); this.rotTimer = null; }
    }

    hold() { this.held = true; this.refresh(); clearTimeout(this.holdT); this.holdT = setTimeout(() => { this.held = false; this.refresh(); }, this.seconds * 2); }

    setPaused(p) {
      this.userPaused = p;
      if (this.pauseBtn) { this.pauseBtn.setAttribute('aria-pressed', String(p)); this.pauseBtn.setAttribute('aria-label', p ? 'Play announcements' : 'Pause announcements'); }
      this.refresh();
    }

    go(i) {
      // Re-check schedules before every move, so an expired message never comes back round.
      const before = this.slides.length;
      this.slides = this.slides.filter((s) => this.eligible(s));
      if (this.slides.length !== before) return this.rebuild();
      if (this.slides.length < 2) return;
      const n = this.slides.length, next = ((i % n) + n) % n;
      if (next === this.index) return;
      const cur = this.slides[this.index], nxt = this.slides[next];
      cur.classList.remove('is-on'); cur.classList.add('is-out'); cur.setAttribute('aria-hidden', 'true');
      nxt.classList.remove('is-out'); void nxt.offsetWidth; nxt.classList.add('is-on'); nxt.setAttribute('aria-hidden', 'false');
      setTimeout(() => cur.classList.remove('is-out'), this.duration + 50);
      this.index = next;
      this.applyTheme(nxt);
    }

    tick() {
      let changed = false;
      this.track.querySelectorAll('.gab__slide').forEach((slide) => {
        const clock = slide.querySelector('.gab__clock');
        if (!clock) return;
        let secs;
        if (clock.hasAttribute('data-cutoff')) secs = this.cutoffSecs(slide);
        else {
          secs = +clock.dataset.to - Date.now() / 1000;
          if (secs <= 0) {
            if (slide.dataset.ended && !slide.dataset.endedShown) {
              slide.dataset.endedShown = '1';
              slide.querySelectorAll('.gab__text,.gab__clock,.gab__cta').forEach((el) => el.remove());
              const t = document.createElement('span'); t.className = 'gab__text'; t.textContent = slide.dataset.ended; slide.appendChild(t);
            } else if (!slide.dataset.ended) changed = true;
            return;
          }
        }
        const parts = clockParts(secs, clock.dataset.days !== 'false');
        const bs = clock.querySelectorAll('b');
        if (bs.length !== parts.length) clock.innerHTML = parts.map(([v, u]) => `<b>${pad(v)}${u}</b>`).join('');
        else parts.forEach(([v, u], k) => { const t = pad(v) + u; if (bs[k].textContent !== t) { bs[k].textContent = t; if (!reduce()) { bs[k].classList.remove('is-tick'); void bs[k].offsetWidth; bs[k].classList.add('is-tick'); } } });
        clock.setAttribute('aria-label', clockLabel(parts));
      });
      if (changed) this.rebuild();
    }

    async copy(btn) {
      const code = btn.dataset.code, label = btn.textContent;
      try { await navigator.clipboard.writeText(code); }
      catch (e) { const r = document.createRange(); r.selectNodeContents(btn); const sel = getSelection(); sel.removeAllRanges(); sel.addRange(r); }
      btn.classList.add('is-copied'); btn.textContent = btn.dataset.copied || 'Copied';
      setTimeout(() => { btn.classList.remove('is-copied'); btn.textContent = label; }, 1800);
    }

    dismiss() {
      try { sessionStorage.setItem('gab-dismissed-' + this.dataset.key, '1'); } catch (e) {}
      this.hidden = true;
    }
  }
  customElements.define('gm-announcement-bar', GmAnnouncementBar);
})();
